import React, { useCallback, useState } from 'react';
import throttle from 'lodash/throttle';
import { InView } from 'react-intersection-observer';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

type InfiniteScrollV2Props<T> = {
  fetchNextPage: () => Promise<T>;
  hasNextPage?: boolean;
  root?: Element | null;
  rootMargin?: string;
  /**
   * Set this to `true` to avoid fetching new pages if the component is in view.
   *
   * @default false
   */
  disabled?: boolean;
};

function InfiniteScrollV2<T>({
  fetchNextPage,
  hasNextPage,
  root,
  rootMargin,
  disabled = false,
}: InfiniteScrollV2Props<T>) {
  const loadMore = useCallback(throttle(fetchNextPage, 500), []);
  const [isFetching, setIsFetching] = useState(false);

  if (isFetching) return <LoadingSpinner />;

  if (!hasNextPage) return null;

  const handleOnChange = async (inView: boolean) => {
    if (!inView || disabled) return;

    // when the scrollbar is at the top and new elements are
    // prepended the scrollbar remains at the top, so we scroll
    // down a bit to avoid this behavior.
    if (root) root.scrollBy(0, 2);

    setIsFetching(true);
    await loadMore();
    setIsFetching(false);
  };

  return (
    <InView onChange={handleOnChange} root={root} rootMargin={rootMargin} />
  );
}

export default InfiniteScrollV2;
